/**
 * Vover Admin App
 * Componente principal da aplicação
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, ThemeProvider } from '@mui/material';
import { voverTheme } from '@/theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RefreshProvider } from './contexts/RefreshContext';
import { FilterProvider } from '@/contexts/FilterContext';
import { ProductsProvider } from '@/contexts/ProductsContext';
import { ConfirmationProvider } from './contexts/ConfirmationContext';
import { MessageProvider } from './contexts/MessageContext';
import { AdminLayout } from './components/Layout/AdminLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AuditLogsPage from './pages/AuditLogsPage';
import ImpersonationPage from './pages/ImpersonationPage';
import UsersPage from './pages/UsersPage';
import { FinancialFlowPage } from './pages/FinancialFlowPage';
import { BillingMetricsPage } from './pages/BillingMetricsPage';
import ConfigurationsPage from './pages/ConfigurationsPage';
import NfseObservabilityPage from './pages/NfseObservabilityPage';
import ProductsPage from './pages/ProductsPage';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Se ainda está carregando (verificando autenticação ou fazendo refresh), mostrar loading
  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Se não está autenticado, redirecionar para login preservando a rota atual
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Se está autenticado, mostrar o conteúdo
  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <AdminLayout>
              <DashboardPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <PrivateRoute>
            <AdminLayout>
              <AuditLogsPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/email-config"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/configurations?tab=email" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/impersonation"
        element={
          <PrivateRoute>
            <AdminLayout>
              <ImpersonationPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/platform/products?tab=pricing" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/tenants"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/platform/products?tab=tenants" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/terms"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/platform/products?tab=terms" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/trial-period"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/platform/products?tab=trial" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute>
            <AdminLayout>
              <UsersPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/support-config"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/configurations?tab=support" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/videos"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/platform/products?tab=videos" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/financial-flow"
        element={
          <PrivateRoute>
            <AdminLayout>
              <FinancialFlowPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/billing-metrics"
        element={
          <PrivateRoute>
            <AdminLayout>
              <BillingMetricsPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/nfse-fiscal-config"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/configurations?tab=nfse" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/nfse-observability"
        element={
          <PrivateRoute>
            <AdminLayout>
              <NfseObservabilityPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/configurations"
        element={
          <PrivateRoute>
            <AdminLayout>
              <ConfigurationsPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/platform/products"
        element={
          <PrivateRoute>
            <AdminLayout>
              <ProductsPage />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/platform/credentials"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/platform/products?tab=billing" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/platform/dashboard"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/billing-metrics" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/platform/product-config"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/platform/products?tab=registry" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
      <Route
        path="/platform/onboard"
        element={
          <PrivateRoute>
            <AdminLayout>
              <Navigate to="/platform/products?tab=registry" replace />
            </AdminLayout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={voverTheme}>
      <Router>
        <AuthProvider>
          <RefreshProvider>
            <ProductsProvider>
              <FilterProvider>
                <ConfirmationProvider>
                  <MessageProvider>
                    <AppRoutes />
                  </MessageProvider>
                </ConfirmationProvider>
              </FilterProvider>
            </ProductsProvider>
          </RefreshProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
